/**
 * Core-100 owner direction 10 — request a training-creator or school account.
 *
 * The database decides everything (`platform_account_requests`, migration 20270206002100): whether
 * requests are open at all (closed = invitation only, an operational setting), that you request only
 * for yourself, one pending request per type, and that only OptiBay staff approve. This page shows the
 * answer it is given.
 */
import { useCallback, useEffect, useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

export type AccountRequestType = 'creator' | 'school';
export interface AccountRequestRow {
  id: string;
  request_type: AccountRequestType;
  organisation_name: string;
  status: 'pending' | 'approved' | 'rejected' | 'withdrawn';
  decision_reason: string | null;
  created_at: string;
}

export const REQUEST_TYPE_LABEL: Record<AccountRequestType, string> = {
  creator: 'Training creator',
  school: 'School',
};

/** The database's refusals, in words a person can act on. */
export function describeRequestRefusal(message: string): string {
  if (message.includes('ACCOUNT_REQUESTS_CLOSED')) return 'This account type is by invitation only right now.';
  if (message.includes('platform_account_requests_one_pending')) return 'You already have a request waiting for a decision.';
  if (message.includes('ACCOUNT_ALREADY_HELD')) return 'You already have this kind of account.';
  return 'The request was not sent. Try again.';
}

export default function RequestAccount() {
  const { user } = useAuth();
  const [open, setOpen] = useState<Record<AccountRequestType, boolean> | null>(null);
  const [mine, setMine] = useState<AccountRequestRow[]>([]);
  const [type, setType] = useState<AccountRequestType>('creator');
  const [name, setName] = useState('');
  const [website, setWebsite] = useState('');
  const [message, setMessage] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [sending, setSending] = useState(false);

  const load = useCallback(async () => {
    const [{ data: settings }, { data: requests }] = await Promise.all([
      supabase.from('platform_account_request_settings' as never).select('request_type, requests_open'),
      supabase.from('platform_account_requests' as never)
        .select('id, request_type, organisation_name, status, decision_reason, created_at')
        .order('created_at', { ascending: false }),
    ]);
    const s = { creator: false, school: false } as Record<AccountRequestType, boolean>;
    for (const row of ((settings ?? []) as unknown) as Array<{ request_type: AccountRequestType; requests_open: boolean }>) {
      s[row.request_type] = row.requests_open === true;
    }
    setOpen(s);
    setMine(((requests ?? []) as unknown) as AccountRequestRow[]);
  }, []);
  useEffect(() => { void load(); }, [load]);

  const submit = async () => {
    if (!user?.id || !name.trim()) return;
    setSending(true);
    setError(null);
    const { error: err } = await supabase.from('platform_account_requests' as never).insert({
      request_type: type, requester_user_id: user.id, organisation_name: name.trim(),
      website: website.trim() || null, message: message.trim() || null,
    } as never);
    setSending(false);
    if (err) { setError(describeRequestRefusal(err.message)); return; }
    setName(''); setWebsite(''); setMessage('');
    void load();
  };

  const withdraw = async (id: string) => {
    await supabase.rpc('withdraw_account_request' as never, { p_request_id: id } as never);
    void load();
  };

  return (
    <div className="container mx-auto max-w-2xl space-y-6 p-4">
      <Card>
        <CardHeader>
          <CardTitle>Request a creator or school account</CardTitle>
          <CardDescription>OptiBay reviews every request. You will see the decision here.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex gap-2" role="radiogroup" aria-label="Account type">
            {(['creator', 'school'] as const).map((t) => (
              <Button key={t} type="button" variant={type === t ? 'default' : 'outline'} size="sm" onClick={() => setType(t)}
                role="radio" aria-checked={type === t}>
                {REQUEST_TYPE_LABEL[t]}
              </Button>
            ))}
          </div>
          {open && !open[type] ? (
            <p className="text-sm text-muted-foreground" data-testid="requests-closed">
              {REQUEST_TYPE_LABEL[type]} accounts are by invitation only right now.
            </p>
          ) : (
            <>
              <div className="space-y-1">
                <Label htmlFor="org">{type === 'school' ? 'School or programme name' : 'Your name or business'}</Label>
                <Input id="org" value={name} onChange={(e) => setName(e.target.value.slice(0, 200))} />
              </div>
              <div className="space-y-1">
                <Label htmlFor="web">Website (optional)</Label>
                <Input id="web" value={website} onChange={(e) => setWebsite(e.target.value.slice(0, 300))} />
              </div>
              <div className="space-y-1">
                <Label htmlFor="msg">Tell us about it (optional)</Label>
                <Textarea id="msg" value={message} onChange={(e) => setMessage(e.target.value.slice(0, 2000))} />
              </div>
              {error && <p role="alert" className="text-sm text-destructive">{error}</p>}
              <Button onClick={() => void submit()} disabled={sending || !name.trim()}>
                {sending ? 'Sending…' : 'Send request'}
              </Button>
            </>
          )}
        </CardContent>
      </Card>

      {mine.length > 0 && (
        <Card>
          <CardHeader><CardTitle className="text-base">Your requests</CardTitle></CardHeader>
          <CardContent className="space-y-2">
            {mine.map((r) => (
              <div key={r.id} className="flex items-center justify-between rounded-md border p-3" data-testid="my-account-request">
                <div>
                  <p className="text-sm font-medium">{REQUEST_TYPE_LABEL[r.request_type]} · {r.organisation_name}</p>
                  {r.status === 'rejected' && r.decision_reason && (
                    <p className="text-xs text-muted-foreground">Declined: {r.decision_reason}</p>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <Badge variant={r.status === 'approved' ? 'default' : 'outline'}>{r.status}</Badge>
                  {r.status === 'pending' && (
                    <Button variant="ghost" size="sm" onClick={() => void withdraw(r.id)}>Withdraw</Button>
                  )}
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
